import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * TwIconButton — square icon-only button. Phase 3: rewritten from inline
 * style objects to Tailwind classes (same look/API); hover states moved from
 * React state to hover: utilities.
 */
const VARIANT_CLASSES = {
  primary:
    'tw-bg-slate-900 tw-text-white tw-border-slate-900 hover:tw-bg-black hover:tw-border-black',
  secondary:
    'tw-bg-white tw-text-slate-600 tw-border-slate-200 hover:tw-bg-primary-50 hover:tw-text-primary-600 hover:tw-border-primary-200',
  ghost:
    'tw-bg-transparent tw-text-slate-500 tw-border-transparent hover:tw-bg-primary-50 hover:tw-text-primary-600',
  soft:
    'tw-bg-slate-100 tw-text-slate-700 tw-border-slate-100 hover:tw-bg-primary-100 hover:tw-text-primary-600 hover:tw-border-primary-100',
  danger:
    'tw-bg-white tw-text-slate-500 tw-border-slate-200 hover:tw-bg-red-50 hover:tw-text-red-600 hover:tw-border-red-200',
  active:
    'tw-bg-primary-600 tw-text-white tw-border-primary-600 hover:tw-bg-primary-700 hover:tw-border-primary-700',
  overlay:
    'tw-bg-white/95 tw-text-slate-900 tw-border-slate-900/10 hover:tw-bg-primary-600 hover:tw-text-white hover:tw-border-primary-600',
  overlayDanger:
    'tw-bg-white/95 tw-text-slate-900 tw-border-slate-900/10 hover:tw-bg-red-600 hover:tw-text-white hover:tw-border-red-600'
};

const SIZE_CLASSES = {
  xs: 'tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6',
  sm: 'tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6',
  md: 'tw-w-7 tw-h-7 tw-min-w-7 tw-min-h-7',
  lg: 'tw-w-9 tw-h-9 tw-min-w-9 tw-min-h-9'
};

const ICON_SIZES = { xs: 9, sm: 10, md: 12, lg: 14 };

const TwIconButton = React.forwardRef(
  (
    {
      icon: Icon,
      iconSize,
      variant = 'secondary',
      size = 'md',
      active = false,
      disabled = false,
      className = '',
      onClick,
      title,
      type = 'button',
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={title}
      className={classNames(
        'ptl-tap',  // finger-sized hit area on touch; no effect with a mouse
        'tw-inline-flex tw-items-center tw-justify-center tw-p-0 tw-shrink-0 tw-leading-none',
        'tw-rounded-lg tw-border tw-border-solid tw-transition-colors tw-duration-100',
        'disabled:tw-opacity-40 disabled:tw-cursor-not-allowed',
        VARIANT_CLASSES[active ? 'active' : variant] || VARIANT_CLASSES.secondary,
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        className
      )}
      {...rest}
    >
      <Icon size={iconSize || ICON_SIZES[size] || ICON_SIZES.md} />
    </button>
  )
);

TwIconButton.displayName = 'TwIconButton';

TwIconButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  iconSize: PropTypes.number,
  variant: PropTypes.oneOf(Object.keys(VARIANT_CLASSES)),
  size: PropTypes.oneOf(Object.keys(SIZE_CLASSES)),
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  title: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset'])
};

export default TwIconButton;
